import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, ProgressBar, OverlayTrigger, Tooltip, Modal, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";

export default function InventaireEnCours() {
    const navigate = useNavigate();
    const [inventaires, setInventaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchInventaires();
    }, []);

    const fetchInventaires = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/inventaires.php?statut=brouillon',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setInventaires(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatutBadge = (statut) => {
        return (
            <Badge bg="warning" className="px-3 py-2 rounded-pill">
                <i className="ri-time-line me-1"></i>
                Brouillon
            </Badge>
        );
    };

    const filteredInventaires = inventaires.filter(i => 
        i.reference_inventaire?.toLowerCase().includes(filterText.toLowerCase())
    );

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredInventaires.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredInventaires.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <>
            {/* Filtres */}
            <Card className="filter-card border-0 mb-4">
                <Card.Body>
                    <Row className="g-3 align-items-center">
                        <Col md={6}>
                            <InputGroup className="search-input">
                                <InputGroup.Text className="bg-white border-end-0">
                                    <i className="ri-search-line text-muted"></i>
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Rechercher un inventaire..."
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    className="border-start-0"
                                />
                                {filterText && (
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => setFilterText('')}
                                        className="border-0"
                                    >
                                        <i className="ri-close-line"></i>
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={6} className="text-end">
                            <span className="text-muted">
                                <i className="ri-stack-line me-1"></i>
                                {filteredInventaires.length} inventaire(s) trouvé(s)
                            </span>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Liste avec pagination */}
            <Card className="table-card border-0">
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="mb-0 align-middle custom-table">
                            <thead>
                                <tr>
                                    <th>Référence</th>
                                    <th>Date</th>
                                    <th>Créé par</th>
                                    <th className="text-center">Produits</th>
                                    <th className="text-center">Progression</th>
                                    <th>Statut</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 ? (
                                    currentItems.map((inv, index) => (
                                        <tr key={inv.id_inventaire} style={{
                                            animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                        }}>
                                            <td>
                                                <Link to={`/stock/inventaire/${inv.id_inventaire}`} className="text-primary fw-semibold">
                                                    {inv.reference_inventaire}
                                                </Link>
                                            </td>
                                            <td>{formatDate(inv.date_inventaire)}</td>
                                            <td>{inv.prenom_utilisateur} {inv.nom_utilisateur}</td>
                                            <td className="text-center">
                                                <Badge bg="info" className="px-3 py-2">
                                                    {inv.nb_produits || 0}
                                                </Badge>
                                            </td>
                                            <td className="text-center" style={{ minWidth: '120px' }}>
                                                <ProgressBar 
                                                    now={65} 
                                                    variant="warning"
                                                    style={{ height: '8px', width: '100px', margin: '0 auto' }}
                                                />
                                            </td>
                                            <td>{getStatutBadge(inv.statut)}</td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Continuer</Tooltip>}>
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            className="rounded-circle"
                                                            style={{ width: '36px', height: '36px' }}
                                                            onClick={() => navigate(`/stock/inventaire/${inv.id_inventaire}`)}
                                                        >
                                                            <i className="ri-play-line"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <i className="ri-inbox-line fs-1 text-muted mb-3"></i>
                                            <h5>Aucun inventaire en cours</h5>
                                            <p className="text-muted">Commencez un nouvel inventaire</p>
                                            <Button variant="primary" onClick={() => navigate('/stock/inventaire/nouveau')}>
                                                <i className="ri-add-line me-2"></i>Nouvel inventaire
                                            </Button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                            <div className="text-muted small mb-2 mb-md-0">
                                <i className="ri-file-list-line me-1"></i>
                                Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredInventaires.length)} sur {filteredInventaires.length} inventaires
                            </div>
                            <Pagination className="mb-0 flex-wrap justify-content-center">
                                <Pagination.First 
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev 
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                />
                                {renderPagination()}
                                <Pagination.Next 
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last 
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .filter-card {
                    border-radius: 16px;
                    background: #f8fafc;
                }

                .table-card {
                    border-radius: 16px;
                    overflow: hidden;
                }

                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 1rem 0.75rem;
                }

                .custom-table tbody td {
                    padding: 1rem 0.75rem;
                    vertical-align: middle;
                }

                .custom-table tbody tr {
                    transition: all 0.2s ease;
                }

                .custom-table tbody tr:hover {
                    background: #f1f5f9 !important;
                }

                .search-input .form-control:focus {
                    box-shadow: none;
                    border-color: #dee2e6;
                }

                /* Styles pour la pagination */
                .pagination {
                    gap: 5px;
                }
                
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                    box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                }
                
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
            `}</style>
        </>
    );
}